import { Project, useProjectsService } from 'nekst-api';
import {
  AfterSubmitFunc,
  DateInput, FormBlock,
  FormLayoutProps,
  FormTemplate,
  Row6x6x,
  TextInput
} from 'ui-builder';
import React, { ReactElement } from 'react';
import { ProjectPlanOfTypeDropdown } from './ProjectPlanOfTypeDropdown';
import { ProjectTypeChangeBehavior } from './ProjectTypeChangeBehavior';
import { ProjectStatusDropdown } from './ProjectStatusDropdown';
import { ParseDataFieldWrapper } from '../Launching/FromContract/ParseDataFieldWrapper';
import { SetupAreaConstrainedProjectTypeDropdown, TeamSetupAreaIdDropdown } from '../../Shared';
import { MultiWorkflowToggleWrapper } from 'feature-toggle';
import { SecondaryWorkflowsDropdown } from './SecondaryWorkflowsDropdown';

export interface BaseProjectFormProps {
  submitFormFunc: (data: Project) => Promise<Project>,
  afterSubmitFunc?: AfterSubmitFunc<Project>,
  initialData?: Project,
  buttons?: ReactElement,
  className?: string
  layout?: React.FC<FormLayoutProps>
  title?: string
  testId?: string
  children?: ReactElement,
  showChooseMarketDropdown?: boolean
}

export function BaseProjectForm(props: BaseProjectFormProps) {
  const projectsService = useProjectsService();

  return (
    <FormTemplate<Project>
      submitFormFunc={props.submitFormFunc}
      afterSubmitFunc={props.afterSubmitFunc}
      validationSchema={{
        _setupArea: {
          type: 'object',
          constraints: {
            required: true,
          },
          properties: {
            id: {
              type: 'int',
              constraints: {
                required: true,
              }
            }
          }
        },
        ...projectsService.getCreateValidationSchema()
      }}
      title={props.title}
      buttons={props.buttons}
      className={props.className}
      layout={props.layout}
      testID={props.testId}
      initialData={props.initialData}
    >
      <FormBlock>
        {props.showChooseMarketDropdown && <TeamSetupAreaIdDropdown source="_setupArea.id"  />}
        <ParseDataFieldWrapper source="name">
          <TextInput source="name" label="Transaction Name" />
        </ParseDataFieldWrapper>
        <SetupAreaConstrainedProjectTypeDropdown
          source="type"
          label="Type"
        />
        <ProjectPlanOfTypeDropdown
          projectTypeSource="type"
          setupAreaIdSource="_setupArea.id"
          source="planId"
          label="Workflow"
        />
        <MultiWorkflowToggleWrapper>
          <SecondaryWorkflowsDropdown
            source="secondaryPlanIds"
            label="Secondary Workflows"
            projectTypeSource="type"
            primaryPlanSource="planId"
            setupAreaIdSource="_setupArea.id"
          />
        </MultiWorkflowToggleWrapper>
        <ProjectTypeChangeBehavior />
        <ProjectStatusDropdown
          source="status"
          label="Status"
          isVisible={(data) => !!data.id}
        />
        <Row6x6x testId="project-start-end-date-fields">
          <ParseDataFieldWrapper source="startDate">
            <DateInput source="startDate" label="Start Date" />
          </ParseDataFieldWrapper>
          <ParseDataFieldWrapper source="endDate">
            <DateInput source="endDate" label="End Date" />
          </ParseDataFieldWrapper>
        </Row6x6x>
        {props.children}
      </FormBlock>
    </FormTemplate>
  );
}
